/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { AppThemeApi, AppTheme } from '@backstage/core-plugin-api';
import { Observable } from '@backstage/types';
import { BehaviorSubject } from '../../../lib/subjects';

const STORAGE_KEY = 'theme';

/**
 * Exposes the themes installed in the app, and permits switching the currently
 * active theme.
 *
 * @public
 */
export class AppThemeSelector implements AppThemeApi {
  static createWithStorage(themes: AppTheme[]) {
    const selector = new AppThemeSelector(themes);

    if (!window.localStorage) {
      return selector;
    }

    const initialThemeId =
      window.localStorage.getItem(STORAGE_KEY) ?? undefined;

    selector.setActiveThemeId(initialThemeId);

    const subscription = selector.activeThemeId$().subscribe(themeId => {
      if (themeId) {
        window.localStorage.setItem(STORAGE_KEY, themeId);
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    });

    const storageListener = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        const themeId = localStorage.getItem(STORAGE_KEY) ?? undefined;
        selector.setActiveThemeId(themeId);
      }
    };
    window.addEventListener('storage', storageListener);

    // Store cleanup references for potential disposal
    selector.#storageSubscription = subscription;
    selector.#storageListener = storageListener;

    return selector;
  }

  private activeThemeId: string | undefined;
  private readonly subject = new BehaviorSubject<string | undefined>(undefined);

  // References for cleanup when using createWithStorage
  #storageSubscription?: { unsubscribe(): void };
  #storageListener?: (event: StorageEvent) => void;

  constructor(private readonly themes: AppTheme[]) {}

  getInstalledThemes(): AppTheme[] {
    return this.themes.slice();
  }

  activeThemeId$(): Observable<string | undefined> {
    return this.subject;
  }

  getActiveThemeId(): string | undefined {
    return this.activeThemeId;
  }

  setActiveThemeId(themeId?: string): void {
    this.activeThemeId = themeId;
    this.subject.next(themeId);
  }

  /**
   * Cleans up resources created by createWithStorage().
   * Call this method when the selector is no longer needed to prevent memory leaks.
   * This is particularly useful in testing scenarios or when the app is unmounted.
   */
  dispose(): void {
    if (this.#storageSubscription) {
      this.#storageSubscription.unsubscribe();
      this.#storageSubscription = undefined;
    }
    if (this.#storageListener) {
      window.removeEventListener('storage', this.#storageListener);
      this.#storageListener = undefined;
    }
  }
}
